import type { MeasurementDefinitionDetail } from '@/src/dal/session-dal'
import { useMemo } from 'react'
import { Text, View } from 'react-native'
import { measurementQueryOptions } from '@/src/queries/measurement-queries'
import { backendSessionQueryOptions } from '@/src/queries/session-queries'
import { colors } from '@/src/theme'
import { body3, body4, label1 } from '@/src/theme/typography'
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { VoiceAssessmentQuestionAnswer } from '../../utils/build-voice-assessment-measurement-answers'
import { buildVoiceAssessmentMeasurementAnswers } from '../../utils/build-voice-assessment-measurement-answers'
import { VoiceAssessmentRecordingPlaceholder } from './VoiceAssessmentRecordingPlaceholder'

export interface VoiceAssessmentExpandedDetailContentLoaderProps {
  readonly measurementDefinitions: readonly MeasurementDefinitionDetail[]
  readonly sessionId: number
  readonly showTitles?: boolean
}

function AnswerRow({
  answer,
  questionIndex,
}: {
  answer: VoiceAssessmentQuestionAnswer
  questionIndex: number
}) {
  const { t } = useTranslation(['statistics'])
  const title = answer.question.title?.trim() || null
  const value =
    answer.selectedOptionLabels.length > 0
      ? answer.selectedOptionLabels.join(', ')
      : t('statistics:voice_assessment_placeholder_dash')

  return (
    <View className="w-[48.4%] rounded-lg bg-background-secondary px-4 py-3">
      <Text style={[body4, { color: colors.textBody, marginBottom: 4 }]}>
        {title ??
          t('statistics:voice_assessment_question_title', {
            n: questionIndex + 1,
          })}
      </Text>
      <Text style={[label1, { color: colors.textHeader }]}>{value}</Text>
    </View>
  )
}

export function VoiceAssessmentExpandedDetailContentLoader({
  measurementDefinitions,
  sessionId,
  showTitles = true,
}: VoiceAssessmentExpandedDetailContentLoaderProps) {
  const { t } = useTranslation(['statistics'])
  const { data: session } = useSuspenseQuery(
    backendSessionQueryOptions(sessionId),
  )

  const measurementsDetailsQueryOptions = session.measurements.map(
    (measurement) => measurementQueryOptions(measurement.id),
  )

  const measurementsDetails = useSuspenseQueries({
    queries: measurementsDetailsQueryOptions,
    combine: (results) => results.map((result) => result.data),
  })

  const combinedMeasurements = useMemo(
    () =>
      buildVoiceAssessmentMeasurementAnswers({
        measurementDefinitions,
        measurementDetails: measurementsDetails,
      }),
    [measurementDefinitions, measurementsDetails],
  )

  return (
    <View className="gap-3">
      {combinedMeasurements.map((measurement) => {
        const title = measurement.definition.name.trim()

        return (
          <View
            key={measurement.definition.id}
            className={`gap-3 rounded-lg p-3 ${measurement.recordingSource == null ? 'bg-secondary700' : 'bg-background-primary'}`}
          >
            {showTitles ? (
              <Text style={[body3, { color: colors.textBody }]}>{title}</Text>
            ) : null}
            {measurement.recordingSource != null ? (
              <VoiceAssessmentRecordingPlaceholder
                recordingSource={measurement.recordingSource}
                durationLabel={t(
                  'statistics:voice_assessment_placeholder_dash',
                )}
              />
            ) : null}
            {measurement.answers.length > 0 ? (
              <View className="flex-row flex-wrap justify-between gap-y-3">
                {measurement.answers.map((answer, answerIndex) => (
                  <AnswerRow
                    key={`${measurement.definition.id}-${answer.question.id}-${answerIndex}`}
                    answer={answer}
                    questionIndex={answerIndex}
                  />
                ))}
              </View>
            ) : (
              <Text style={[label1, { color: colors.textHeader }]}>
                {t('statistics:voice_assessment_placeholder_dash')}
              </Text>
            )}
          </View>
        )
      })}
    </View>
  )
}
